// Assessment Data Normalization Utilities

import {
  GENDER_MAP,
  RACE_MAP,
  ACTIVITY_LEVEL_MAP,
  WEIGHT_LOSS_GOAL_MAP,
  INTERMITTENT_FASTING_MAP,
  FASTING_WINDOW_MAP,
} from "../types/assessmentTypes";
import { ASSESSMENT_API_CONFIG } from "../config/assessmentConfig";

// Gender normalization
export function normalizeGender(gender?: string): string {
  if (!gender) {
    return ASSESSMENT_API_CONFIG.DEFAULTS.GENDER;
  }

  if (GENDER_MAP[gender]) {
    return GENDER_MAP[gender];
  }

  const lowerGender = gender.toLowerCase();
  if (lowerGender.includes("male") && !lowerGender.includes("female")) {
    return "male";
  }
  if (lowerGender.includes("female")) {
    return "female";
  }
  return "other";
}

// Race normalization
export function normalizeRace(race?: string): string {
  if (!race) {
    return ASSESSMENT_API_CONFIG.DEFAULTS.RACE;
  }

  if (RACE_MAP[race]) {
    return RACE_MAP[race];
  }

  const lowerRace = race.toLowerCase();
  if (lowerRace.includes("black") || lowerRace.includes("african")) {
    return "black_african_american";
  }
  if (lowerRace.includes("white") || lowerRace.includes("caucasian")) {
    return "white";
  }
  if (lowerRace.includes("asian")) {
    return "asian";
  }
  if (
    lowerRace.includes("native") ||
    lowerRace.includes("indian") ||
    lowerRace.includes("alaska")
  ) {
    return "american_indian_alaska_native";
  }
  if (lowerRace.includes("hawaiian") || lowerRace.includes("pacific")) {
    return "native_hawaiian_pacific_islander";
  }
  if (lowerRace.includes("middle") || lowerRace.includes("eastern")) {
    return "middle_eastern_north_african";
  }
  return "other";
}

// Activity level normalization
export function normalizeActivityLevel(activity?: string): string {
  if (!activity) {
    return ASSESSMENT_API_CONFIG.DEFAULTS.ACTIVITY_LEVEL;
  }

  if (ACTIVITY_LEVEL_MAP[activity]) {
    return ACTIVITY_LEVEL_MAP[activity];
  }

  const lowerActivity = activity.toLowerCase();
  if (lowerActivity === "sedentary" || activity === "sedentary") {
    return "sedentary";
  }
  if (lowerActivity.includes("lightly") || lowerActivity.includes("light")) {
    return "lightly_active";
  }
  if (
    lowerActivity.includes("moderately") ||
    lowerActivity.includes("moderate")
  ) {
    return "moderately_active";
  }
  if (lowerActivity.includes("very") || lowerActivity.includes("highly")) {
    return "very_active";
  }
  if (lowerActivity.includes("extremely") || lowerActivity.includes("super")) {
    return "extremely_active";
  }
  return "sedentary";
}

// Weight loss goal normalization
export function normalizeWeightLossGoal(goal?: string): string {
  if (!goal) {
    return ASSESSMENT_API_CONFIG.DEFAULTS.WEIGHT_LOSS_GOAL;
  }

  if (WEIGHT_LOSS_GOAL_MAP[goal]) {
    return WEIGHT_LOSS_GOAL_MAP[goal];
  }

  const lowerGoal = goal.toLowerCase();
  if (lowerGoal.includes("yes")) {
    return "yes";
  }
  if (lowerGoal.includes("not") || lowerGoal.includes("sure")) {
    return "not_sure";
  }
  return "no";
}

// Intermittent fasting normalization
export function normalizeIntermittentFasting(
  fasting?: string | boolean
): string {
  if (!fasting) {
    return ASSESSMENT_API_CONFIG.DEFAULTS.INTERMITTENT_FASTING;
  }

  if (typeof fasting === "string" && INTERMITTENT_FASTING_MAP[fasting]) {
    return INTERMITTENT_FASTING_MAP[fasting];
  }

  if (typeof fasting === "boolean") {
    return fasting
      ? "Yes — I follow a fasting window"
      : "No — I eat throughout the day";
  }

  if (typeof fasting === "string") {
    if (fasting === "true") {
      return "Yes — I follow a fasting window";
    }
    if (fasting === "false") {
      return "No — I eat throughout the day";
    }

    const lowerFasting = fasting.toLowerCase();
    if (
      lowerFasting.includes("yes") ||
      lowerFasting.includes("follow") ||
      lowerFasting.includes("window")
    ) {
      return "Yes — I follow a fasting window";
    }
    if (lowerFasting.includes("sometimes") || lowerFasting.includes("sure")) {
      return "Sometimes / Not sure";
    }
    if (
      lowerFasting.includes("no") ||
      lowerFasting.includes("don't") ||
      lowerFasting.includes("throughout")
    ) {
      return "No — I eat throughout the day";
    }
  }

  return "No — I eat throughout the day";
}

// Fasting window type normalization
export function normalizeFastingWindowType(windowType?: string): string {
  if (!windowType) {
    return ASSESSMENT_API_CONFIG.DEFAULTS.FASTING_WINDOW_TYPE;
  }

  if (FASTING_WINDOW_MAP[windowType]) {
    return FASTING_WINDOW_MAP[windowType];
  }

  const lowerWindow = windowType.toLowerCase();
  if (lowerWindow.includes("16:8") || lowerWindow.includes("16")) {
    return "16:8 (Fast 16 hrs / Eat 8 hrs)";
  }
  if (lowerWindow.includes("18:6") || lowerWindow.includes("18")) {
    return "18:6";
  }
  if (lowerWindow.includes("14:10") || lowerWindow.includes("14")) {
    return "14:10";
  }
  if (lowerWindow.includes("custom")) {
    return "Custom";
  }
  if (lowerWindow.includes("varies")) {
    return "My eating window varies";
  }
  return "16:8 (Fast 16 hrs / Eat 8 hrs)";
}

// Height unit normalization
export function normalizeHeightUnit(unit?: string): string {
  if (!unit) {
    return ASSESSMENT_API_CONFIG.DEFAULTS.HEIGHT_UNIT;
  }

  if (
    unit.toLowerCase().includes("cm") ||
    unit.toLowerCase().includes("centimeter")
  ) {
    return "cm";
  }
  return "ft_in";
}

// Weight unit normalization
export function normalizeWeightUnit(unit?: string): string {
  if (!unit) {
    return ASSESSMENT_API_CONFIG.DEFAULTS.WEIGHT_UNIT;
  }

  if (
    unit.toLowerCase().includes("lb") ||
    unit.toLowerCase().includes("pound")
  ) {
    return "lbs";
  }
  return "kg";
}
